import React, { useState } from 'react';
import { updateUser, User, PERMISSION_FIELDS } from '../../../features/user/services/usersAPI';
import styles from './UserFormModal.module.css';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User;
}

const EditUserModal: React.FC<EditUserModalProps> = ({ isOpen, onClose, onSuccess, user }) => {
  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (isOpen && user) {
      setNome(user.NOME || '');
      setSenha('');
      // Preenche permissões a partir dos campos do usuário
      const perms: Record<string, boolean> = {};
      for (const p of PERMISSION_FIELDS) {
        perms[p.key] = (user as any)[p.key] === 1;
      }
      setPermissions(perms);
      setError('');
    }
  }, [isOpen, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Constrói payload com permissões 0/1
      const permValues: Record<string, number> = {};
      for (const [key, val] of Object.entries(permissions)) {
        permValues[key] = val ? 1 : 0;
      }

      await updateUser(user.ID_USUARIO, {
        USUARIO: user.USUARIO,
        NOME: nome,
        SENHA: senha || user.SENHA,
        ...permValues,
      } as any);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2>Editar Usuário</h2>
          <button className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <div className={styles.error}>{error}</div>}

          <div className={styles.grid}>
            <div className={styles.field}>
              <label className={styles.label}>Nome completo</label>
              <input
                type="text"
                className={styles.input}
                value={nome}
                onChange={e => setNome(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Usuário (login)</label>
              <input
                type="text"
                className={styles.input}
                value={user.USUARIO}
                disabled
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Senha (deixe vazio para manter)</label>
              <input
                type="text"
                className={styles.input}
                value={senha}
                onChange={e => setSenha(e.target.value)}
                placeholder="Nova senha"
              />
            </div>
          </div>

          {/* Permissões */}
          <div className={styles.permissionsBlock}>
            <span className={styles.permissionsTitle}>Permissões de Acesso</span>
            <div className={styles.permissionsGrid}>
              {PERMISSION_FIELDS.map(perm => (
                <label key={perm.key} className={styles.permissionItem}>
                  <input
                    type="checkbox"
                    className={styles.permissionCheckbox}
                    checked={permissions[perm.key] || false}
                    onChange={e => setPermissions(prev => ({ ...prev, [perm.key]: e.target.checked }))}
                  />
                  <div className={styles.permissionText}>
                    <span className={styles.permissionLabel}>{perm.label}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>Cancelar</button>
            <button type="submit" className={styles.saveBtn} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditUserModal;